import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from "@angular/core";
import { SearchParameterService } from "app_dashboard/service/search-parameter.service";
import { Yii } from "libraries/interfaces/Yii";
import { IDateFieldSpecs } from "libraries/module/formbuilder/fields/interfaces/field-specs";
import {
    ISimpleSelectFieldConfig,
    ISimpleSelectOption
} from "libraries/module/formbuilder/fields/simple-select/simple-select.interface";
import { FormControl, FormGroup } from "@angular/forms";
import {
    DashboardResourceService,
    IGetFilterResponse
} from "libraries/service/resources/dashboard/dashboard-resource.service";
import {
    IDashboardFilters,
    IDashboardFilterFormField, IOptionsWTranslatedString
} from "libraries/interfaces/i-dashboard-config";
import { BtnStyleTypes } from "libraries/ui_button/btn.component";
import { ITranslatedString } from "libraries/interfaces/IContract";
import { LocalizedPipe } from "libraries/pipes/localized/localized.pipe";
import _cloneDeep from "lodash-es/cloneDeep";
import { Subscription } from "rxjs";

class Wording {
    public search: string = Yii.t('wui-sasha-js', "Actualiser");
}

@Component({
    selector: "search-header",
    template: `
        <h1>{{dashboardName}}</h1>
        <div class="header" *ngIf="fieldConfigs?.length">
            <div>
                <ng-container *ngFor="let fieldConfig of fieldConfigs">
                    <ng-container *ngIf="fieldConfig.type === 'simpleSelect'">
                        <multiselect class="normal"
                                     [maxSelection]="1"
                                     [showHeader]="false"
                                     [options]="fieldConfig.options"
                                     [fieldConfig]="fieldConfig"></multiselect>
                    </ng-container>
                    <ng-container *ngIf="fieldConfig.type === 'multiSelect'">
                        <multiselect class="normal"
                                     [options]="fieldConfig.options"
                                     [showHeader]="false"
                                     [fieldConfig]="fieldConfig"></multiselect>
                    </ng-container>
                    <ng-container
                            *ngIf="fieldConfig.type === 'date'">
                        <date-field class="normal"
                                    [fieldConfig]="fieldConfig"></date-field>
                    </ng-container>
                </ng-container>
                <btn [label]="labels.search"
                     [type]="btnType"
                     class="normal"
                     (onClick)="search()"></btn>
            </div>
        </div>
    `,
    styles: [`
        h1 {
            background-color: #fff;
            padding: .75rem;
        }
        .header > div {
            display: flex;
            height: 100%;
            align-items: flex-end;
            background: white;
            padding-bottom: .75rem;

        }

        .normal {
            flex: 1;
            margin-right: .75rem;
            text-align: center;
        }
        .normal:first-of-type {
            /*flex: 1;*/
            margin-left: .75rem;
            /*text-align: center;*/
        }
    `],
})
export class SearchHeaderComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    public dashboardFiltersConfiguration: IDashboardFilters;
    @Input()
    public dashboardCriteriaConfiguration: IDashboardFilters;

    @Input()
    public index: string;
    @Input()
    public dashboardName: string;

    @Output()
    public refreshData: EventEmitter<any> = new EventEmitter<any>();

    public labels: Wording = new Wording();

    public display: boolean = false;
    public filterOptions: { [key: string]: ISimpleSelectOption[] } = {};

    public fieldConfigs: Array<ISimpleSelectFieldConfig | IDateFieldSpecs>;
    public btnType: BtnStyleTypes = BtnStyleTypes.actionPrincipal;

    private form: FormGroup = new FormGroup({});
    private subscription: Subscription;

    constructor(
        private readonly searchParameterService: SearchParameterService,
        private readonly dashboardResource: DashboardResourceService,
        private readonly localizedPipe: LocalizedPipe,
    ) {
    }

    public ngOnInit(): void {
        this.display = false;
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes?.dashboardFiltersConfiguration?.currentValue) {
            this.initFormControl();
            this.request();
        }
    }

    public ngOnDestroy(): void {
        if (this.subscription && !this.subscription.closed) {
            this.subscription.unsubscribe();
        }
    }

    public search(): void {
        this.searchParameterService.setParams(this.form.value);
        this.refreshData.emit();
    }

    public request(): void {
        if (this.subscription && !this.subscription.closed) {
            this.subscription.unsubscribe();
        }
        this.subscription =
            this.dashboardResource.getFilters(
                {
                    index: this.index,
                    ...this.searchParameterService.getSearchParams(),
                    req: this.dashboardFiltersConfiguration.requestFile,
                }
            )
                .subscribe({
                    next: (val: IGetFilterResponse): void => {
                        if (val) {
                            Object.keys(val)
                                .forEach((key: string): void => {
                                    this.filterOptions[key] = val[key]
                                        .map((item: string): ISimpleSelectOption => {
                                            return {
                                                value: item,
                                                label: item,
                                            };
                                        });
                                });
                        } else {
                            this.filterOptions = {};
                        }
                        this.initFormFieldConfig();
                        this.display = true;
                    },
                    error: (): void => {
                        this.filterOptions = {};
                        this.initFormFieldConfig();
                        this.display = true;
                    }
                });
    }

    private initFormControl(): void {
        _cloneDeep(this.dashboardFiltersConfiguration.formFields)
            .map((field: IDashboardFilterFormField): IDashboardFilterFormField => {
                return {
                    ...field,
                    code: this.searchParameterService.replaceDot(field.code)
                };
            })
            .forEach((field: IDashboardFilterFormField): void => {
                const value: string | Date | string[] = this.searchParameterService.getParam(field.code);
                let values: string[] = [];
                if (Boolean(value) && (field.type === 'multiSelect' || field.type === 'simpleSelect')) {
                    values = (value as string).split(',');
                    const firstOption: string = field.optionArray?.length ? field.optionArray[0].value : null;
                    this.form.setControl(field.code, new FormControl(values || firstOption || ""));
                } else {
                    this.form.setControl(field.code, new FormControl(value));
                }
            });
        this.dashboardCriteriaConfiguration.formFields
            .forEach((field: IDashboardFilterFormField): void => {
                const value: string | Date | string[] = this.searchParameterService.getParam(field.code, field.optionArray);
                let values: string[] = [];
                if (value && (field.type === 'multiSelect' || field.type === 'simpleSelect')) {
                    values = (value as string).split(',');
                    const firstOption: string = field.optionArray?.length ? field.optionArray[0].value : null;
                    this.form.setControl(field.code, new FormControl(values || firstOption || ""));
                } else {
                    this.form.setControl(field.code, new FormControl(value));
                }

            });

    }

    private initFormFieldConfig(): void {
        this.fieldConfigs = [
            ...this.dashboardFiltersConfiguration.formFields
                .map((field: IDashboardFilterFormField): ISimpleSelectFieldConfig => this.computeFieldSpecFromConfig(field)),
            ...this.dashboardCriteriaConfiguration.formFields
                .map((field: IDashboardFilterFormField): ISimpleSelectFieldConfig | IDateFieldSpecs => this.computeFieldSpecFromConfig(field)),
        ];
    }

    private computeFieldSpecFromConfig(field: IDashboardFilterFormField, useFilterOptions?: boolean): ISimpleSelectFieldConfig | IDateFieldSpecs {
        const label: ITranslatedString[] = field.titleArray;

        let options: ISimpleSelectOption[];
        if (field.type === 'simpleSelect' || field.type === 'multiSelect') {
            options = field.optionArray ?
                field.optionArray.map((optionTranslation: IOptionsWTranslatedString): ISimpleSelectOption => {
                    return {
                        value: optionTranslation.value,
                        label: this.localizedPipe.transform(optionTranslation.labelArray),
                    };
                }) :
                this.filterOptions[field.code] ?? [];
        }


        return {
            type: field.type,
            formGroup: this.form,
            formControlName: this.searchParameterService.replaceDot(field.code),
            label: this.localizedPipe.transform(label),
            className: "with-border",
            options
        };
    }
}
